import { useCallback, useEffect } from 'react';
import {
  useClearRefinements,
  useHits,
  useRefinementList } from
'react-instantsearch';
import clsx from 'clsx';
import { type RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';

import ScrollableRow from '~/components/Header/lib/components/scrollable-row';
import { Pill } from '~/components/OddscheckerPlus/components/cxy';
import { type BettingInsights } from '~/configuration/betting-insights/BettingInsightsConfig';
import { type EsIndex } from '~/configuration/betting-insights/types';
import { withLock } from '~/hocs/withLock';

import { trackCategoryRefinementClick } from '../../tracking';

import styles from './styles/CategoriesRefinement.module.css';

export function CategoriesRefinement<TIndex extends EsIndex>({
  categories: { attribute, labels, order },
  hasResults,
  isDisabled = false






}: {categories: ReturnType<BettingInsights<TIndex>['ui']['results']['categories']>;hasResults: boolean;isDisabled?: boolean;}) {
  const allText = 'All';

  const { items: hits } = useHits();
  const { items, refine } = useRefinementList({
    attribute,
    limit: 25
  });
  const { refine: clearRefinement } = useClearRefinements({
    includedAttributes: [attribute]
  });

  const toggleCategory = useCallback(
    (category: string) => {
      trackCategoryRefinementClick({ selection: category });
      const selected = items.find(
        (item) => item.isRefined && item.value === category
      );

      if (!selected) {
        clearRefinement();
      }

      if (category !== allText) {
        refine(category);
      }
    },
    [clearRefinement, items, refine]
  );

  useEffect(() => {
    if (!hits.length) {
      clearRefinement();
    }
  }, [clearRefinement, hits]);

  const sortedCategories = sortItemsByOrder(items, order);

  return (
    <section className={styles.categories} data-testid="categories">
			<ScrollableRow className={styles.sportFilter} showArrows={!isDisabled}>
				{sortedCategories.length > 1 &&
        <CategoryPill
          active={items.every((item) => !item.isRefined)}
          disabled={isDisabled || !hasResults && !items.length}
          onClick={() => toggleCategory(allText)}>

						{allText}
					</CategoryPill>
        }
				{sortedCategories.map((item) =>
        <CategoryPill
          active={sortedCategories.length === 1 || item.isRefined}
          disabled={isDisabled}
          key={item.value}
          onClick={() => toggleCategory(item.value)}>

						{labels?.[item.value]?.displayName ?? item.label}
					</CategoryPill>
        )}
			</ScrollableRow>
		</section>);

}

const CategoryPill = ({
  active,
  children,
  disabled,
  onClick





}: {active: boolean;children: React.ReactNode;disabled: boolean;onClick: () => void;}) =>
<Pill
  active={active}
  className={clsx(styles.pill, { [styles.active]: active })}
  disabled={disabled}
  onClick={onClick}>

		{children}
	</Pill>;


function sortItemsByOrder(
items: RefinementListItem[],
order: {
  end?: string[];
  start: string[];
})
{
  // Split items into start, end and other categories
  const startItems = items.filter((item) => order.start.includes(item.value));
  const endItems = items.filter((item) => order.end?.includes(item.value));
  const otherItems = items.filter(
    (item) =>
    !order.start.includes(item.value) && !order.end?.includes(item.value)
  );

  // Sort start items by their position in order.start
  const sortedStartItems = [...startItems].sort((a, b) => {
    const aIndex = order.start.indexOf(a.value);
    const bIndex = order.start.indexOf(b.value);
    return aIndex - bIndex;
  });

  // Sort end items by their position in order.end
  const sortedEndItems = [...endItems].sort((a, b) => {
    const aIndex = (order.end ?? []).indexOf(a.value);
    const bIndex = (order.end ?? []).indexOf(b.value);
    return aIndex - bIndex;
  });

  // Sort other items alphabetically
  const sortedOtherItems = [...otherItems].sort((a, b) =>
  a.value.localeCompare(b.value)
  );

  // Combine all sorted arrays in order
  return [...sortedStartItems, ...sortedOtherItems, ...sortedEndItems];
}

export const LockableCategoriesRefinement = withLock(
  CategoriesRefinement,
  'componentWall',
  'categories'
);